import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import AdminLayout from "./AdminLayout";
import {
  getAllProducts,
  addProduct,
  updateProduct,
  toggleProductActive,
  deleteProduct,
} from "../../firebase/productService";
import { CATEGORIES } from "../../firebase/constants";

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

// ─── Layout ───────────────────────────────────────────────────────────────────
const PageHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 48px;
  margin-top: 3em;
`;

const PageTitle = styled.h1`
  color: #fff;
  font-size: 2rem;
  font-weight: 400;
  letter-spacing: 0.05em;
  margin: 0 0 4px 0;
`;

const PageSub = styled.p`
  color: #444;
  font-size: 0.72rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  margin: 0;
`;

// ─── Buttons ──────────────────────────────────────────────────────────────────
const PrimaryBtn = styled.button`
  background: #fff;
  color: #0a0a0a;
  border: none;
  padding: 12px 24px;
  font-size: 0.72rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  cursor: pointer;
  font-family: "Georgia", serif;
  transition: background 0.2s;

  &:hover {
    background: #ccc;
  }
  &:disabled {
    background: #333;
    color: #666;
    cursor: not-allowed;
  }
`;

const GhostBtn = styled.button`
  background: none;
  border: 1px solid #333;
  color: #666;
  padding: 8px 16px;
  font-size: 0.65rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  cursor: pointer;
  font-family: "Georgia", serif;
  transition: all 0.2s;

  &:hover {
    color: #ccc;
    border-color: #666;
  }
`;

const DangerBtn = styled(GhostBtn)`
  &:hover {
    color: #c0392b;
    border-color: #c0392b;
  }
`;

// ─── Product Grid ─────────────────────────────────────────────────────────────
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 48px;
`;

const Card = styled.div`
  background: #111;
  border: 1px solid #1e1e1e;
  animation: ${fadeIn} 0.4s ease forwards;
  opacity: ${(p) => (p.$inactive ? 0.4 : 1)};
  transition: opacity 0.3s;
`;

const CardImage = styled.div`
  width: 100%;
  height: 180px;
  background: #0a0a0a;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const CardBody = styled.div`
  padding: 16px;
`;

const CardName = styled.h3`
  color: #fff;
  font-size: 0.95rem;
  font-weight: 400;
  margin: 0 0 4px 0;
`;

const CardCategory = styled.span`
  color: #555;
  font-size: 0.65rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
`;

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  padding: 12px 16px;
  border-top: 1px solid #1e1e1e;
`;

const StatusBadge = styled.span`
  font-size: 0.6rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  padding: 3px 8px;
  border: 1px solid ${(p) => (p.$active ? "#2ecc71" : "#444")};
  color: ${(p) => (p.$active ? "#2ecc71" : "#555")};
  margin-left: auto;
  align-self: center;
`;

const EmptyState = styled.div`
  color: #333;
  font-size: 0.85rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  text-align: center;
  padding: 80px 0;
  border: 1px dashed #1e1e1e;
`;

// ─── Modal Overlay ────────────────────────────────────────────────────────────
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 100;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 20px;
  overflow-y: auto;
`;

const Modal = styled.div`
  background: #111;
  border: 1px solid #222;
  width: 100%;
  max-width: 740px;
  padding: 40px;
  animation: ${fadeIn} 0.3s ease;
`;

const ModalTitle = styled.h2`
  color: #fff;
  font-size: 1.2rem;
  font-weight: 400;
  letter-spacing: 0.08em;
  margin: 0 0 32px 0;
`;

// ─── Form Elements ────────────────────────────────────────────────────────────
const FormRow = styled.div`
  display: grid;
  grid-template-columns: ${(p) => p.$cols || "1fr"};
  gap: 20px;
  margin-bottom: 24px;
`;

const Field = styled.div``;

const Label = styled.label`
  display: block;
  color: #666;
  font-size: 0.68rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  background: #0a0a0a;
  border: 1px solid #2a2a2a;
  color: #fff;
  padding: 10px 12px;
  font-size: 0.9rem;
  font-family: "Georgia", serif;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s;

  &:focus {
    border-color: #555;
  }
`;

const Select = styled.select`
  width: 100%;
  background: #0a0a0a;
  border: 1px solid #2a2a2a;
  color: #fff;
  padding: 10px 12px;
  font-size: 0.9rem;
  font-family: "Georgia", serif;
  outline: none;
  box-sizing: border-box;
  cursor: pointer;
  transition: border-color 0.2s;

  &:focus {
    border-color: #555;
  }

  option {
    background: #111;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  background: #0a0a0a;
  border: 1px solid #2a2a2a;
  color: #fff;
  padding: 10px 12px;
  font-size: 0.9rem;
  font-family: "Georgia", serif;
  outline: none;
  box-sizing: border-box;
  resize: vertical;
  min-height: 80px;
  transition: border-color 0.2s;

  &:focus {
    border-color: #555;
  }
`;

const ImagePreview = styled.div`
  width: 100%;
  height: 140px;
  background: #0a0a0a;
  border: 1px dashed #2a2a2a;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: pointer;
  margin-top: 8px;
  transition: border-color 0.2s;

  &:hover {
    border-color: #555;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  span {
    color: #444;
    font-size: 0.7rem;
    letter-spacing: 0.15em;
    text-transform: uppercase;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

// ─── Pricing Grid ─────────────────────────────────────────────────────────────
const PricingSection = styled.div`
  margin-top: 8px;
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #1e1e1e;
`;

const ErrorMsg = styled.p`
  color: #c0392b;
  font-size: 0.75rem;
  margin: 0 0 16px 0;
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const emptyForm = () => ({
  name: "",
  category: CATEGORIES[0],
  description: "",
});

// ─── Main Component ───────────────────────────────────────────────────────────
const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // null = add mode
  const [form, setForm] = useState(emptyForm());
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = React.useRef();

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const data = await getAllProducts();
      setProducts(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // ── Open modal ──────────────────────────────────────────────────────────────
  const openAdd = () => {
    setEditTarget(null);
    setForm(emptyForm());
    setImageFile(null);
    setImagePreview(null);
    setError("");
    setShowModal(true);
  };

  const openEdit = (product) => {
    setEditTarget(product);
    setForm({
      name: product.name,
      category: product.category,
      description: product.description || "",
      imagePath: product.imagePath || "",
    });
    setImageFile(null);
    setImagePreview(product.imageUrl || null);
    setError("");
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditTarget(null);
  };

  // ── Form field changes ──────────────────────────────────────────────────────
  const handleField = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  // ── Save ────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.name.trim()) {
      setError("Product name is required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (editTarget) {
        await updateProduct(editTarget.id, form, imageFile);
      } else {
        await addProduct(form, imageFile);
      }
      await fetchProducts();
      closeModal();
    } catch (err) {
      console.error(err);
      setError("Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // ── Toggle active ───────────────────────────────────────────────────────────
  const handleToggle = async (product) => {
    try {
      await toggleProductActive(product.id, product.active);
      await fetchProducts();
    } catch (err) {
      console.error(err);
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────────────
  const handleDelete = async (product) => {
    if (!window.confirm(`Delete "${product.name}"? This cannot be undone.`))
      return;
    try {
      await deleteProduct(product.id, product.imagePath);
      await fetchProducts();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <AdminLayout>
      <PageHeader>
        <div>
          <PageTitle>Products</PageTitle>
          <PageSub>{products.length} items in collection</PageSub>
        </div>
        <PrimaryBtn onClick={openAdd}>+ Add Product</PrimaryBtn>
      </PageHeader>

      {loading ? (
        <EmptyState>Loading...</EmptyState>
      ) : products.length === 0 ? (
        <EmptyState>No products yet. Add your first one.</EmptyState>
      ) : (
        <Grid>
          {products.map((p) => (
            <Card key={p.id} $inactive={!p.active}>
              <CardImage>
                {p.imageUrl ? (
                  <img src={p.imageUrl} alt={p.name} />
                ) : (
                  <span
                    style={{
                      color: "#333",
                      fontSize: "0.65rem",
                      letterSpacing: "0.1em",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      textTransform: "uppercase",
                    }}
                  >
                    No Image
                  </span>
                )}
              </CardImage>
              <CardBody>
                <CardName>{p.name}</CardName>
                <CardCategory>{p.category}</CardCategory>
              </CardBody>
              <CardActions>
                <GhostBtn onClick={() => openEdit(p)}>Edit</GhostBtn>
                <GhostBtn onClick={() => handleToggle(p)}>
                  {p.active ? "Deactivate" : "Activate"}
                </GhostBtn>
                <DangerBtn onClick={() => handleDelete(p)}>Delete</DangerBtn>
                <StatusBadge $active={p.active}>
                  {p.active ? "Live" : "Off"}
                </StatusBadge>
              </CardActions>
            </Card>
          ))}
        </Grid>
      )}

      {/* ── Modal ── */}
      {showModal && (
        <Overlay onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <Modal>
            <ModalTitle>
              {editTarget ? "Edit Product" : "Add New Product"}
            </ModalTitle>

            <FormRow $cols="1fr 1fr">
              <Field>
                <Label>Product Name</Label>
                <Input
                  name="name"
                  value={form.name}
                  onChange={handleField}
                  placeholder="e.g. Morning Mist"
                />
              </Field>
              <Field>
                <Label>Category</Label>
                <Select
                  name="category"
                  value={form.category}
                  onChange={handleField}
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Select>
              </Field>
            </FormRow>

            <FormRow>
              <Field>
                <Label>Description (optional)</Label>
                <Textarea
                  name="description"
                  value={form.description}
                  onChange={handleField}
                  placeholder="A short description of this piece..."
                />
              </Field>
            </FormRow>

            <FormRow>
              <Field>
                <Label>Product Image</Label>
                <ImagePreview onClick={() => fileInputRef.current.click()}>
                  {imagePreview ? (
                    <img src={imagePreview} alt="preview" />
                  ) : (
                    <span>Click to upload image</span>
                  )}
                </ImagePreview>
                <HiddenInput
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImage}
                />
              </Field>
            </FormRow>

            {error && (
              <ErrorMsg style={{ marginTop: "16px" }}>{error}</ErrorMsg>
            )}

            <ModalFooter>
              <GhostBtn onClick={closeModal}>Cancel</GhostBtn>
              <PrimaryBtn onClick={handleSave} disabled={saving}>
                {saving
                  ? "Saving..."
                  : editTarget
                    ? "Save Changes"
                    : "Add Product"}
              </PrimaryBtn>
            </ModalFooter>
          </Modal>
        </Overlay>
      )}
    </AdminLayout>
  );
};

export default AdminProducts;
